import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ActiveStay, PastStay, STAY_TYPE } from 'src/features/stay/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { useLiveStayDuration } from 'src/hooks/useLiveStayDuration';
import { commonMessages } from 'src/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import { PATH } from 'src/features/navigation/utils/constants';
import { validationMessages } from 'src/features/validations/utils/messages';

type StayOverviewProps = {
    type: STAY_TYPE;
    selectedStay?: ActiveStay | PastStay;
};

const OverviewActiveStays = ({ type, selectedStay }: StayOverviewProps) => {
    const { formatMessage, formatNumber } = useIntl();

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    // When already viewing this visitor's dashboard, a link would lead back to the same page —
    // render the visitor as plain text instead of a link in that case.
    const { visitorId } = useParams();
    const isCurrentVisitorDashboard = !!visitorId && visitorId === selectedStay?.visitor.id;

    const durationToDisplay = useLiveStayDuration(selectedStay?.entry_time, selectedStay?.exit_time);

    const translatedVisitorType = selectedStay?.visitor.type
        ? `(${formatMessage(visitorTypeBadgeOptions[selectedStay?.visitor.type].label).toLowerCase()})`
        : '';

    const formattedEntryTime = getFormattedDateTimeForDetailPanel(selectedStay?.entry_time);
    const ownerName = selectedStay?.visitor.owner?.name;
    const ownerNameDisplay = ownerName ? `- ${ownerName}` : '';
    const visitorDetails = `${selectedStay?.main_identifier.value} ${translatedVisitorType} ${ownerNameDisplay}`;

    const totalRemainingAmount = selectedStay?.total_amounts?.total_remaining_amount
        ? formatNumber(selectedStay?.total_amounts?.total_remaining_amount / 100, {
              style: 'currency',
              currency: selectedStay?.currency,
          })
        : undefined;

    const totalDueAmount = (selectedStay as PastStay)?.total_due_amount
        ? formatNumber((selectedStay as PastStay)?.total_due_amount / 100, {
              style: 'currency',
              currency: selectedStay?.currency,
          })
        : undefined;

    const paymentDue = type === STAY_TYPE.ACTIVE ? totalRemainingAmount : totalDueAmount;

    const totalPaidAmount = selectedStay?.total_amounts?.total_paid_amount
        ? formatNumber(selectedStay?.total_amounts?.total_paid_amount / 100, {
              style: 'currency',
              currency: selectedStay?.currency,
          })
        : undefined;

    const totalValidatedAmount = selectedStay?.total_amounts?.total_validated_amount
        ? formatNumber(selectedStay?.total_amounts?.total_validated_amount / 100, {
              style: 'currency',
              currency: selectedStay?.currency,
          })
        : undefined;

    const validatedTime = formatMessage(commonMessages.minutes, {
        value: selectedStay?.total_amounts?.total_validated_time,
    });

    const additionalIdentifiers = selectedStay?.additional_identifiers.map((identifier) => identifier.value).join(', ');

    const getExitTime = () => {
        if (selectedStay && 'exit_time' in selectedStay) {
            return getFormattedDateTimeForDetailPanel(selectedStay.exit_time);
        }
        return '-';
    };

    return (
        <div className="mt-4 grid gap-4 sm:gap-6">
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.visitor)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay?.main_identifier.value} fallbackMessage="-">
                    <LinkWithIcon
                        to={selectedStay?.visitor.id ? `/${PATH.VISITORS}/visitor/${selectedStay?.visitor.id}` : ''}
                        linkTitle={visitorDetails}
                        hasPermissions={accessVisitors && !isCurrentVisitorDashboard}
                    />
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    {formatMessage(visitorMessages.additionalIdentifiers)}
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody
                    control={selectedStay?.additional_identifiers?.length !== 0}
                    fallbackMessage="-"
                >
                    {additionalIdentifiers ?? '-'}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.paymentDue)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={paymentDue} fallbackMessage="-">
                    {totalRemainingAmount}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.paidAmount)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={totalPaidAmount} fallbackMessage="-">
                    {totalPaidAmount}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    {formatMessage(visitorMessages.validatedAmount)}
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={totalValidatedAmount} fallbackMessage="-">
                    {totalValidatedAmount}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    {formatMessage(validationMessages.validatedTime)}
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={validatedTime} fallbackMessage="-">
                    {validatedTime}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.enteredAt)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay?.entry_time} fallbackMessage="-">
                    {formattedEntryTime}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.exitAt)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay} fallbackMessage="-">
                    {getExitTime()}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.duration)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody
                    control={durationToDisplay}
                    fallbackMessage={formatMessage(commonMessages.minutes, { value: 0 })}
                >
                    {durationToDisplay}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        </div>
    );
};

export default OverviewActiveStays;
